import { type DragEndEvent, type DragDropManager } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { move } from '@dnd-kit/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import {
  expertReportKmKeys,
  useMoveKmAssessment,
  useMoveKmGroup,
  type KmStructureResponse,
} from '@/entities/expert-report-km';
import { computeDropBand } from '../ExpertReportTree/treeUtils';
import {
  buildKmSortableGroups,
  collectKmSubtreeIds,
  FIRST_KM_ORDER_POSITION,
  kmReportNodeId,
  moveAssessmentInStructure,
  moveGroupInStructure,
  parseKmNodeId,
  resolveKmDropPlace,
  type KmDropPlace,
  type KmTreeNodeData,
} from './kmTreeUtils';

type Params = {
  reportId: number;
  /** Корневая группа структуры — контейнер верхнего уровня заключения */
  rootGroupId: number | null;
  /** Узлы дерева структуры в текущем порядке */
  nodes: KmTreeNodeData[];
  /** Идентификаторы узлов-групп без вложенных элементов — только они принимают вложение внутрь */
  leafGroupIds: Set<string>;
};

/**
 * Перетаскивание в дереве структуры заключения на модели знаний.
 *
 * Итоговое место узла вычисляется один раз при отпускании: вложение внутрь пустой группы —
 * по середине её строки, остальное — хелпером `move`. Позиция считается среди узлов того же
 * вида: порядки групп и единиц оценивания в контейнере независимы. Новый порядок применяется
 * к структуре сразу, а при отказе сервера возвращается снимок, снятый в начале перетаскивания.
 */
export const useKmStructureDnd = ({ reportId, rootGroupId, nodes, leafGroupIds }: Params) => {
  const queryClient = useQueryClient();
  const { mutate: moveGroup } = useMoveKmGroup();
  const { mutate: moveAssessment } = useMoveKmAssessment();
  const snapshotRef = useRef<KmStructureResponse | null>(null);

  const structureKey = expertReportKmKeys.structure({ reportId });
  const rootNodeId = kmReportNodeId(reportId);

  const handleDragStart = useCallback(() => {
    snapshotRef.current = queryClient.getQueryData<KmStructureResponse>(structureKey) ?? null;
  }, [queryClient, structureKey]);

  const applyMove = useCallback(
    (nodeId: string, place: KmDropPlace, snapshot: KmStructureResponse) => {
      const parsed = parseKmNodeId(nodeId);
      if (!parsed) return;

      const current = queryClient.getQueryData<KmStructureResponse>(structureKey) ?? snapshot;
      const optimistic =
        parsed.type === 'group'
          ? moveGroupInStructure(current, parsed.entityId, place)
          : moveAssessmentInStructure(current, parsed.entityId, place);
      queryClient.setQueryData(structureKey, optimistic);

      const onError = () => queryClient.setQueryData(structureKey, snapshot);

      if (parsed.type === 'group') {
        moveGroup(
          { groupId: parsed.entityId, body: { parentId: place.parentGroupId, orderPosition: place.orderPosition } },
          { onError },
        );
        return;
      }

      // Единица оценивания всегда лежит в группе: верхний уровень заключения — это его
      // корневая группа, и в переносе её нужно назвать явно, иначе группа не сменится
      const targetGroupId = place.parentGroupId ?? rootGroupId;
      if (targetGroupId === null) return;

      moveAssessment(
        { assessmentId: parsed.entityId, body: { groupId: targetGroupId, orderPosition: place.orderPosition } },
        { onError },
      );
    },
    [moveAssessment, moveGroup, queryClient, rootGroupId, structureKey],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent, manager: DragDropManager) => {
      const snapshot = snapshotRef.current;
      snapshotRef.current = null;
      if (event.canceled || !snapshot) return;

      const { source, target } = event.operation;
      if (!source || !isSortable(source) || !target) return;

      const sourceId = String(source.id);
      const targetId = String(target.id);
      const descendants = collectKmSubtreeIds(nodes, sourceId);

      // Вложение внутрь пустой группы — середина её строки
      if (leafGroupIds.has(targetId) && targetId !== sourceId && !descendants.has(targetId)) {
        const rect = manager.dragOperation.target?.shape?.boundingRectangle;
        const pointerY = manager.dragOperation.position.current?.y;
        const band = rect && pointerY != null ? computeDropBand(pointerY, rect.top, rect.height, true) : null;
        if (band === 'nest') {
          const targetGroup = parseKmNodeId(targetId);
          if (targetGroup?.type === 'group') {
            applyMove(
              sourceId,
              { parentGroupId: targetGroup.entityId, orderPosition: FIRST_KM_ORDER_POSITION },
              snapshot,
            );
          }
          return;
        }
      }

      const groups = buildKmSortableGroups(nodes, rootNodeId);
      const nextGroups = move(groups, event);
      if (nextGroups === groups) return;

      const place = resolveKmDropPlace(nextGroups, sourceId, rootNodeId);
      if (!place) return;

      // Группу нельзя перенести внутрь собственного поддерева — это замкнуло бы структуру
      if (place.parentGroupId !== null && descendants.has(`kmgroup-${reportId}-${place.parentGroupId}`)) return;

      applyMove(sourceId, place, snapshot);
    },
    [applyMove, leafGroupIds, nodes, reportId, rootNodeId],
  );

  return { handleDragStart, handleDragEnd };
};
